import { useState } from 'react';

import { fetchHtmlServerSide, fetchStoreApiText } from '../../services/firebase';
import { Button } from '../common/Button';
import { MA_SOURCE_LABEL, type MaSource } from './fieldSpec';
import {
  PLATFORM_VERDICT_LABELS,
  claimedPlatform,
  isPlatformMismatch,
  verifyStorePlatform,
} from './platformProbe';
import { agoLabel } from './proposalCachePolicy';
import type { DetailEntry } from './sessionCache';
import { recordPlatformCheck, refreshMaBrandDetail } from './useMaBrandDetail';

interface ProposalCacheBarProps {
  slug: string;
  entry: DetailEntry;
  ma: MaSource;
}

/**
 * Above the comparison grid: where the proposal's data came from and how old
 * it is (with Refresh), and the "Verify platform" probe with its last result.
 */
export function ProposalCacheBar({ slug, entry, ma }: ProposalCacheBarProps) {
  const [verifying, setVerifying] = useState(false);
  const [persistNote, setPersistNote] = useState<string | null>(null);
  const website = ma.website_url ?? '';
  const claimed = claimedPlatform(ma);
  const check = entry.platformCheck ?? null;
  const mismatch = check ? isPlatformMismatch(claimed, check.verdict) : false;

  const verify = async () => {
    setVerifying(true);
    setPersistNote(null);
    try {
      const { verdict, siteUrl } = await verifyStorePlatform(website, {
        fetchShopify: (url) => fetchHtmlServerSide(url),
        fetchWoo: fetchStoreApiText,
      });
      const saved = await recordPlatformCheck(slug, { verdict, siteUrl, checkedAt: new Date() });
      if (!saved) setPersistNote('Result shown for this session only — it could not be saved.');
    } finally {
      setVerifying(false);
    }
  };

  const sourceLine =
    entry.source === 'stale'
      ? null
      : entry.source === 'cache' && entry.fetchedAt
        ? `Cached ${agoLabel(entry.fetchedAt)} from ${MA_SOURCE_LABEL}`
        : `Fetched from ${MA_SOURCE_LABEL} just now`;

  return (
    <div className="space-y-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm">
      {entry.source === 'stale' ? (
        <div role="status" className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
          Showing the copy cached {entry.fetchedAt ? agoLabel(entry.fetchedAt) : 'earlier'} — {entry.liveProblem}.
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {sourceLine && <span className="text-gray-600">{sourceLine}</span>}
        <Button variant="ghost" size="sm" onClick={() => refreshMaBrandDetail(slug)}>
          Refresh
        </Button>
        <span className="mx-1 h-4 w-px bg-gray-200" aria-hidden />
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void verify()}
          disabled={verifying || !website}
          title={website ? `Probe ${website} for Shopify and WooCommerce` : 'This proposal has no website to probe'}
        >
          {verifying ? 'Verifying…' : 'Verify platform'}
        </Button>
        {check && (
          <span className="text-gray-700">
            <span className="font-medium">{PLATFORM_VERDICT_LABELS[check.verdict]}</span>
            <span className="text-gray-500"> · checked {agoLabel(check.checkedAt)}</span>
          </span>
        )}
        {mismatch && (
          <span className="rounded bg-red-100 px-1.5 py-0.5 text-[11px] font-medium text-red-700">
            Proposal claims {claimed === 'shopify' ? 'Shopify' : 'WooCommerce'}
          </span>
        )}
      </div>
      {persistNote && <p className="text-xs text-gray-500">{persistNote}</p>}
    </div>
  );
}
